import { engineComponent } from "neo-common";
import { BaseController } from "./BaseController";
import { Paylines } from "../Paylines";
import { PaylinesSelector } from "../PaylinesSelector";
import { Symbols } from "../Symbols";
import { WinAnimator } from "../WinAnimator";
import { WinAnimatorExtended } from "../../CustomComponents/WinAnimatorExtended";
import { ComponentTypes } from "../../Components/ComponentTypes";
import { ContainerElement } from "../../Components/Elements/Graphics/ContainerElement";
import { PlayList } from "../../Components/Sounds/PlayList";
import { DataManager } from "../../Managers/DataManager";
import { GameManager } from "../../Managers/GameManager";
import { CreateFrameTimer, FrameTimerEventEmitter, FrameTimerEvents } from "../../Utils/FrameTimer";
import { SwitchElement } from "../../Components/Elements/Graphics/SwitchElement";
import { CustomEase } from "../../Components/Effects/CustomEase";
import { MachineExtended } from "../../CustomComponents/MachineExtended";
import { ReflectionElement } from "../../CustomComponents/ReflectionElement";
import { ImageElement } from "../../Components/Elements/Graphics/ImageElement";
import { SceneHolderElement } from "../../CustomComponents/SceneHolderElement";
import { ModuleManager } from "../../Managers/ModuleManager";
import Container = PIXI.Container;
import { IReel } from "neo-games/Engine/Communication/Game/Types/IReel";
import { Tweens } from "../../Components/Effects/Tweens";
import { AmbientBackground } from "../../Utils/AmbientBackground";

@engineComponent(ComponentTypes.DisplayObject)
export class BaseView extends ContainerElement {
    private _controller: BaseController;

    private _wasFinished: boolean = false;

    private _introDelay: FrameTimerEventEmitter;
    private _outroDelay: FrameTimerEventEmitter;
    private _outroFinishDelay: FrameTimerEventEmitter;

    private _paylines: Paylines;
    private _paylinesSelector: PaylinesSelector;
    private _symbols: Symbols;
    private _machine: MachineExtended;
    private _winAnimator: WinAnimatorExtended;

    private _reflectionContainer: ReflectionElement;
    private _gambleReflectionHolder: SwitchElement;

    private _mosaicElements: Array<SwitchElement>;
    private _colorElements: Array<SwitchElement>;
    private _backgroundSymbolContainer: Array<SwitchElement>;
    private _backgroundSymbols: Array<Array<SwitchElement>>;

    private _currentBaseLoopVolume: number;
    private _currentBaseLoopLayerVolume: number;

    constructor() {
        super();

        this._introDelay = CreateFrameTimer("introDelay");
        this._introDelay.on(FrameTimerEvents.Complete, this.introFinished.bind(this));

        this._outroDelay = CreateFrameTimer("outroDelay");
        this._outroDelay.on(FrameTimerEvents.Complete, this.outroStart.bind(this));

        this._outroFinishDelay = CreateFrameTimer("outroFinishDelay");
        this._outroFinishDelay.on(FrameTimerEvents.Complete, this.outroFinished.bind(this));

        this._mosaicElements = [];
        this._colorElements = [];
        this._backgroundSymbolContainer = [];

        this._currentBaseLoopVolume = 0.2;
        this._currentBaseLoopLayerVolume = 0;

        //create own custom easing functions we used in game
        CustomEase._createOwnEasing();
    }

    createByNode(node: Element): void {
        super.createByNode(node);

        if (this.childs != null) {
            for (let key in this.childs) {
                if (this.childs[key] instanceof Paylines) {
                    this._paylines = this.childs[key];
                }

                if (this.childs[key] instanceof PaylinesSelector) {
                    this._paylinesSelector = this.childs[key];
                }

                if (this.childs[key] instanceof Symbols) {
                    this._symbols = this.childs[key];
                    this.removeChild(this._symbols);
                }

                if (this.childs[key] instanceof MachineExtended) {
                    this._machine = this.childs[key];
                }

                if (this.childs[key] instanceof WinAnimatorExtended) {
                    this._winAnimator = this.childs[key];
                }

                if (key == "Reflected" && this.childs[key] instanceof ReflectionElement) {
                    this._reflectionContainer = this.childs[key];
                }

                if (key == "GambleReflectionHolder" && this.childs[key] instanceof SwitchElement) {
                    this._gambleReflectionHolder = this.childs[key];
                }

                if (key == "Background" && this.childs[key] instanceof ContainerElement) {
                    let background: ContainerElement = this.childs[key];

                    if (background.childs["Mosaic"]) {
                        this._mosaicElements.push(background.childs["Mosaic"]);
                    }

                    if (background.childs["MosaicReflect"]) {
                        this._mosaicElements.push(background.childs["MosaicReflect"]);
                    }

                    if (background.childs["BackgroundSymbols"]) {
                        this._backgroundSymbolContainer.push(background.childs["BackgroundSymbols"]);

                        this._backgroundSymbols = [];
                        for (let i = 0; i < 3; i++) {
                            this._backgroundSymbols.push([]);

                            for (let j = 0; j < 6; j++) {
                                this._backgroundSymbols[i].push(
                                    <SwitchElement>(
                                        this._backgroundSymbolContainer[0].getChildElementByName("S" + i + "_" + j)
                                    ),
                                );
                            }
                        }
                    }

                    if (background.childs["BackgroundSymbolsReflect"]) {
                        this._backgroundSymbolContainer.push(background.childs["BackgroundSymbolsReflect"]);

                        if (this._backgroundSymbols.length > 0) {
                            for (let i = 0; i < 3; i++) {
                                for (let j = 0; j < 6; j++) {
                                    this._backgroundSymbols[i].push(
                                        <SwitchElement>(
                                            this._backgroundSymbolContainer[1].getChildElementByName("S" + i + "_" + j)
                                        ),
                                    );
                                }
                            }
                        }
                    }

                    if (background.childs["Colors"]) {
                        this._colorElements.push(background.childs["Colors"]);
                    }

                    if (background.childs["ColorsReflect"]) {
                        this._colorElements.push(background.childs["ColorsReflect"]);
                    }

                    if (background.childs["BaseLights"]) {
                        this._colorElements.push(background.childs["BaseLights"]);
                    }
                }

                if (this.childs["SyncedReelsBg"]) {
                    let syncedReels = <ContainerElement>this.childs["SyncedReelsBg"];

                    for (let i = 0; i < 5; i++) {
                        let syncedReel = <SwitchElement>syncedReels.childs["SyncedReel" + i];

                        if (syncedReel) {
                            let syncColorLight = <SwitchElement>syncedReel.getChildElementByName("Light");
                            let syncColorBg = <SwitchElement>syncedReel.getChildElementByName("Bg");

                            if (syncColorLight && syncColorBg) {
                                this._colorElements.push(syncColorLight, syncColorBg);
                            }
                        }
                    }

                    let lightsLeft = <SwitchElement>(
                        (<SwitchElement>syncedReels.childs["LightsLeft"]).getChildElementByName("Lights")
                    );
                    let lightsRight = <SwitchElement>(
                        (<SwitchElement>syncedReels.childs["LightsRight"]).getChildElementByName("Lights")
                    );

                    if (lightsLeft && lightsRight) {
                        for (let i = 0; i < 26; i++) {
                            let lightLeft = <SwitchElement>lightsLeft.getChildElementByName("Light" + i);
                            let lightRight = <SwitchElement>lightsRight.getChildElementByName("Light" + i);

                            if (lightLeft && lightRight) {
                                this._colorElements.push(lightLeft, lightRight);
                            }
                        }
                    }
                }
            }

            if (DEBUG) {
                if (this._paylines == null) {
                    this.log("WARNING no paylines");
                }

                if (this._symbols == null) {
                    this.log("ERROR no symbols");
                }

                if (this._machine == null) {
                    this.log("ERROR no machine");
                }

                if (this._winAnimator == null) {
                    this.log("WARNING no winAnimator");
                }
            }
        }
    }

    disableLogo(): void {
        if (this.childs["Logo"] != null) {
            this.removeChild(this.childs["Logo"]);
        }
    }

    initReceived(controller: BaseController): void {
        if (DEBUG) {
            this.log("initReceived");
        }

        this._controller = controller;

        if (this._symbols != null) {
            if (this._machine != null) {
                if (GameManager.baseResponse != null) {
                    if (GameManager.baseResponse.initData != null && GameManager.baseResponse.initData.reels != null) {
                        this._machine.on(MachineExtended.ReelStopped, this.onReelStopped.bind(this));
                        this._machine.on(MachineExtended.MachineStopped, this.machineStopped.bind(this));
                        this._machine.on(MachineExtended.SyncReelShowed, this.onSyncReelUpdate.bind(this));
                        this._machine.on(MachineExtended.SyncReelForceShowed, this.onSyncReelForceUpdate.bind(this));
                        this._machine.initialize(this._symbols, GameManager.baseResponse.initData.reels);
                        this._machine.initializeSyncedReelsBg(
                            this.childs["SyncedReelsBg"],
                            GameManager.baseResponse.screen.reels,
                        );
                        this._machine.preInitOffScreenSymbols();

                        if (
                            GameManager.baseResponse.initData.customData &&
                            GameManager.baseResponse.initData.customData["syncReels"]
                        ) {
                            let syncedStrips = <Array<{ mask: number; symbols: Array<number> }>>(
                                GameManager.baseResponse.initData.customData["syncReels"]
                            );
                            this._machine.initSyncedReelsStrip(syncedStrips);
                        }
                    }

                    if (
                        GameManager.baseResponse.screen != null &&
                        GameManager.baseResponse.screen.reels != null &&
                        GameManager.baseResponse.screen.reels.length !== 0
                    ) {
                        if (this._winAnimator != null) {
                            this._winAnimator.on(
                                WinAnimator.FinishWinCalculation,
                                this.finishWinCalculation.bind(this),
                            );
                            this._winAnimator.on(WinAnimator.FinishWinAnimator, this.finishWinAnimator.bind(this));
                            this._winAnimator.on(
                                WinAnimator.FinishQuickWinCalculation,
                                this.finishQuickWinCalculation.bind(this),
                            );
                            this._winAnimator.on(
                                WinAnimatorExtended.ShowBackgroundSymbols,
                                this.setBackgroundSymbols.bind(this),
                            );
                            this._winAnimator.initialize(
                                this._symbols,
                                this._paylines,
                                this.childs["NoWinContainer"],
                                this.childs["WinContainer"],
                            );
                            this._winAnimator.showSymbols(GameManager.baseResponse.screen.reels);

                            this._colorElements = this._colorElements.concat(this._winAnimator.getColorElements());
                        }

                        this._machine.setStoppedSymbolsArray(GameManager.baseResponse.screen.reels);

                        this.initReflectionFilter();
                        this._winAnimator.setupReflectionElement(this._reflectionContainer, this.childs["ReelsBase"]);
                    } else {
                        if (DEBUG) {
                            this.log("ERROR no reels in response");
                        }
                    }
                } else {
                    if (DEBUG) {
                        this.log("ERROR no base reponse");
                    }
                }
            } else {
                if (DEBUG) {
                    this.log("ERROR no machine");
                }
            }
        } else {
            if (DEBUG) {
                this.log("ERROR no symbols");
            }
        }

        this.setupSceneElement();

        if (ModuleManager.gambleActive()) {
            if (this._gambleReflectionHolder) {
                this._gambleReflectionHolder.goToCase("GAMBLE");
                <SwitchElement>this.childs["ReelsBase"].goToCase("GAMBLE");
            }
        }
    }

    initIntro(): void {
        if (GameManager.gameResponse.totalWin > 0) {
            this.forceScene(GameManager.baseResponse.screen.reels, true);
            PlayList.loop("SND_BaseRecapLoop");
        } else {
            PlayList.loop("SND_BaseLoop");
            PlayList.loop("SND_BaseLoopLayer0");
            this.setSongVolume("SND_BaseLoop", "_currentBaseLoopVolume", 0.2);
            this.setSongVolume("SND_BaseLoopLayer0", "_currentBaseLoopLayerVolume", 0);
        }

        this._winAnimator.showInitWins(GameManager.baseResponse.wins, GameManager.baseResponse.currentWin);
    }

    introFinished(): void {
        if (GameManager.gameResponse.totalWin > 0) {
            this.removeSongFade("_currentBaseLoopVolume");
            PlayList.stop("SND_BaseLoop");
            PlayList.stop("SND_BaseLoopLayer0");
            PlayList.loop("SND_BaseRecapLoop");
        }

        this._controller.introFinished();
    }

    collect(): void {
        PlayList.stop("SND_BaseRecapLoop");
        PlayList.loop("SND_BaseLoop");
        PlayList.loop("SND_BaseLoopLayer0");
        this.setSongVolume("SND_BaseLoop", "_currentBaseLoopVolume", 0.2);
        this.setSongVolume("SND_BaseLoopLayer0", "_currentBaseLoopLayerVolume", 0);

        if (this._paylinesSelector != null) {
            this._paylinesSelector.reset();
        }

        if (this.childs["BackgroundMood"]) {
            this.childs["BackgroundMood"].goToCase("MOOD_1");
        }

        this.onSpinCollectUpdate();
        this._winAnimator.reset(true);
    }

    spin(): void {
        PlayList.stop("SND_BaseRecapLoop");
        PlayList.loop("SND_BaseLoop");
        PlayList.loop("SND_BaseLoopLayer0");
        this.setSongVolume("SND_BaseLoopLayer0", "_currentBaseLoopLayerVolume", 0);

        if (this._paylinesSelector != null) {
            this._paylinesSelector.reset();
        }

        this.onSpinCollectUpdate();
        this._machine.spinStart(this._winAnimator.reset(true), true, this._winAnimator.currentSymbols);
    }

    stop(): void {
        this._machine.spinStop(null, true);
        this.forceScene(GameManager.baseResponse.screen.reels);
    }

    spinReceived(): void {
        this._machine.setStoppedSymbolsArray(GameManager.baseResponse.screen.reels);
        this._machine.spinStop(null, false, true);
    }

    onReelStopped(): void {
        this._winAnimator.onReelStopped(GameManager.baseResponse.screen.reels, this._machine.actualReelStopped);
    }

    machineStopped(): void {
        this._wasFinished = false;

        this._controller.machineStopped();

        this.fadeSong("SND_BaseLoop", "_currentBaseLoopVolume", 300, 0.2);
        this.fadeSong("SND_BaseLoopLayer0", "_currentBaseLoopLayerVolume", 300, 0);

        if (this._winAnimator.showWins(GameManager.baseResponse.wins, GameManager.baseResponse.currentWin)) {
            this.removeSongFade("_currentBaseLoopVolume");
            this.removeSongFade("_currentBaseLoopLayerVolume");
            PlayList.stop("SND_BaseLoop");
            PlayList.stop("SND_BaseLoopLayer0");
            this.setSongVolume("SND_BaseLoop", "_currentBaseLoopVolume", 0.2);
            this.setSongVolume("SND_BaseLoopLayer0", "_currentBaseLoopLayerVolume", 0);
        }
    }

    private finishQuickWinCalculation(): void {
        this._controller.quickWinCalculationFinished();
    }

    finishWinCalculation(): void {
        this._wasFinished = true;

        this._controller.winCalculationFinished();
    }

    finishWinAnimator(): void {
        if (!DataManager.Autoplay) {
            this.removeSongFade("_currentBaseLoopVolume");
            PlayList.stop("SND_BaseLoop");
            PlayList.stop("SND_BaseLoopLayer0");
            PlayList.loop("SND_BaseRecapLoop");
            this.setSongVolume("SND_BaseLoop", "_currentBaseLoopVolume", 0.2);
        }

        this._controller.allWinsFinished();
    }

    resetWinAnimator(): void {
        this._winAnimator.reset(true);
    }

    intro(long: boolean = false): void {
        PlayList.stop("SND_BaseRecapLoop");
        PlayList.loop("SND_BaseLoop");
        PlayList.loop("SND_BaseLoopLayer0");
        this.setSongVolume("SND_BaseLoop", "_currentBaseLoopVolume", 0.2);
        this.setSongVolume("SND_BaseLoopLayer0", "_currentBaseLoopLayerVolume", 0);

        if (this._gambleReflectionHolder) {
            this._gambleReflectionHolder.goToCase("GAMBLE_OUT");
            <SwitchElement>this.childs["ReelsBase"].goToCase("GAMBLE_OUT");
        }

        this._introDelay.start(30);
    }

    outro(): void {
        this.removeSongFade("_currentBaseLoopVolume");
        PlayList.stop("SND_BaseLoop");
        PlayList.stop("SND_BaseLoopLayer0");
        PlayList.stop("SND_BaseRecapLoop");
        this.setSongVolume("SND_BaseLoop", "_currentBaseLoopVolume", 0.2);

        if (this._paylinesSelector != null) {
            this._paylinesSelector.reset();
        }

        let inHighWins = this._winAnimator.reset(true);

        if (this._wasFinished) {
            this.outroStart();
        } else {
            if (inHighWins) {
                this._outroDelay.start(60);
            } else {
                this._outroDelay.start(30);
            }
        }
    }

    outroStart(): void {
        PlayList.play("SND_GambleTransitionIn");

        this.onSpinCollectUpdate();

        if (this._gambleReflectionHolder) {
            this._gambleReflectionHolder.goToCase("GAMBLE_IN");
            <SwitchElement>this.childs["ReelsBase"].goToCase("GAMBLE_IN");
        }

        this._outroFinishDelay.start(30);
    }

    outroFinished(): void {
        this._controller.outroFinished();
    }

    selectPayline(): void {
        if (this._paylinesSelector != null) {
            this._paylinesSelector.select();
        }
    }

    disableBase(): void {
        this._winAnimator.resetTimer();
    }

    /* CUSTOM */

    private onSyncReelUpdate(syncReelsCount: number, isLeftDirection: number): void {
        this.setMosaicCase(isLeftDirection ? "LEFT" : "RIGHT");
        this.setSceneColor(syncReelsCount - 2);

        this.fadeSong("SND_BaseLoop", "_currentBaseLoopVolume", 5, (syncReelsCount - 1) * 0.2);
        this.fadeSong("SND_BaseLoopLayer0", "_currentBaseLoopLayerVolume", 5, (syncReelsCount - 2) * 0.2);
    }

    private onSyncReelForceUpdate(syncReelsCount: number): void {
        this.removeSongFade("_currentBaseLoopVolume");
        this.removeSongFade("_currentBaseLoopLayerVolume");

        this.setSongVolume("SND_BaseLoop", "_currentBaseLoopVolume", 0.2 * syncReelsCount);
        this.setSongVolume("SND_BaseLoopLayer0", "_currentBaseLoopLayerVolume", 0.2 * (syncReelsCount - 1));
    }

    private forceScene(reels: Array<IReel>, init: boolean = false): void {
        let syncReelCount = 0;

        for (let i = 0; i < reels.length; i++) {
            if (reels[i].customData && reels[i].customData["isSynced"]) {
                syncReelCount++;
            }
        }

        this.setMosaicCase("IDLE");
        this.setSceneColor(syncReelCount - 2, init);
    }

    private onSpinCollectUpdate(): void {
        this.setMosaicCase("IDLE");
        this.setSceneColor(0);
        this.resetBackgroundSymbols();
    }

    private setMosaicCase(caseType: string): void {
        for (let i = 0; i < this._mosaicElements.length; i++) {
            this._mosaicElements[i].goToCase(caseType);
        }
    }

    private setSceneColor(sceneColorIndex: number = 0, init: boolean = false): void {
        let caseType = init ? "INIT_COLOR_" : "COLOR_";

        AmbientBackground.changeAmbientBackground(sceneColorIndex == 0 ? "base" : "color-" + sceneColorIndex, 100);

        for (let i = 0; i < this._colorElements.length; i++) {
            if (this._colorElements[i].hasCase(caseType + sceneColorIndex)) {
                this._colorElements[i].goToCase(caseType + sceneColorIndex);
            }
        }
    }

    private setBackgroundSymbols(symbolIds: Array<number>, hasWild: boolean, init: boolean = false): void {
        if (symbolIds.length > 0) {
            let mask = this.getSymbolsMask(symbolIds, hasWild);

            for (let i = 0; i < 3; i++) {
                for (let j = 0; j < 6; j++) {
                    this._backgroundSymbols[i][j].goToCase("S" + mask[i][j]);
                    this._backgroundSymbols[i][j + 6].goToCase("S" + mask[i][j]);
                }
            }

            let caseType = Math.random() > 0.5 ? "WIN_0" : "WIN_1";
            if (init) {
                caseType = "INIT_" + caseType;
            }

            this._backgroundSymbolContainer[0].goToCase(caseType);
            this._backgroundSymbolContainer[1].goToCase(caseType);

            this.setMosaicCase("WIN_" + Math.floor(Math.random() * 4));
        }
    }

    private resetBackgroundSymbols(): void {
        if (this._backgroundSymbolContainer[0].getCurrentCase() != "EMPTY") {
            this._backgroundSymbolContainer[0].goToCase("RESET");
            this._backgroundSymbolContainer[1].goToCase("RESET");
        }
    }

    private getSymbolsMask(symbolIds: Array<number>, hasWild: boolean): Array<Array<number>> {
        let mask: Array<Array<number>>;
        let a: number, b: number, c: number, d: number, e: number, f: number, g: number, h: number;
        let w = 12;
        let random = Math.random();

        switch (symbolIds.length) {
            case 1:
                a = symbolIds[0];
                if (hasWild) {
                    if (random > 0.5) {
                        mask = [
                            [a, w, a, w, a, w],
                            [a, w, a, w, a, w],
                            [a, w, a, w, a, w],
                        ];
                    } else {
                        mask = [
                            [a, a, a, a, a, a],
                            [w, w, w, w, w, w],
                            [a, a, a, a, a, a],
                        ];
                    }
                } else {
                    mask = [
                        [a, a, a, a, a, a],
                        [a, a, a, a, a, a],
                        [a, a, a, a, a, a],
                    ];
                }
                break;
            case 2:
                a = symbolIds[0];
                b = symbolIds[1];

                if (hasWild) {
                    if (random > 0.5) {
                        mask = [
                            [a, a, a, a, a, a],
                            [w, w, w, w, w, w],
                            [b, b, b, b, b, b],
                        ];
                    } else {
                        mask = [
                            [a, w, b, a, w, b],
                            [b, w, a, b, w, a],
                            [a, w, b, a, w, b],
                        ];
                    }
                } else {
                    mask = [
                        [a, b, a, b, a, b],
                        [a, b, a, b, a, b],
                        [a, b, a, b, a, b],
                    ];
                }
                break;
            case 3:
                a = symbolIds[0];
                b = symbolIds[1];
                c = symbolIds[2];

                if (hasWild) {
                    if (random > 0.5) {
                        mask = [
                            [a, w, a, w, a, w],
                            [b, w, b, w, b, w],
                            [c, w, c, w, c, w],
                        ];
                    } else {
                        mask = [
                            [a, b, c, a, b, c],
                            [w, w, w, w, w, w],
                            [c, b, a, c, b, a],
                        ];
                    }
                } else {
                    if (random > 0.5) {
                        mask = [
                            [a, a, a, a, a, a],
                            [b, b, b, b, b, b],
                            [c, c, c, c, c, c],
                        ];
                    } else {
                        mask = [
                            [a, b, c, a, b, c],
                            [c, b, a, c, b, a],
                            [a, b, c, a, b, c],
                        ];
                    }
                }
                break;
            case 4:
                a = symbolIds[0];
                b = symbolIds[1];
                c = symbolIds[2];
                d = symbolIds[3];

                if (hasWild) {
                    if (random > 0.5) {
                        mask = [
                            [a, w, b, a, w, b],
                            [c, w, d, c, w, d],
                            [a, b, w, c, d, w],
                        ];
                    } else {
                        mask = [
                            [a, b, a, b, a, b],
                            [w, w, w, w, w, w],
                            [c, d, c, d, c, d],
                        ];
                    }
                } else {
                    if (random > 0.5) {
                        mask = [
                            [a, b, a, b, a, b],
                            [c, d, c, d, c, d],
                            [a, b, a, b, a, b],
                        ];
                    } else {
                        mask = [
                            [c, d, c, d, c, d],
                            [a, b, a, b, a, b],
                            [c, d, c, d, c, d],
                        ];
                    }
                }
                break;
            case 5:
                a = symbolIds[0];
                b = symbolIds[1];
                c = symbolIds[2];
                d = symbolIds[3];
                e = symbolIds[4];

                if (hasWild) {
                    mask = [
                        [a, b, c, d, e, w],
                        [w, e, d, c, b, a],
                        [a, b, c, d, e, w],
                    ];
                } else {
                    mask = [
                        [a, b, c, a, b, c],
                        [d, e, d, e, d, e],
                        [a, b, c, a, b, c],
                    ];
                }
                break;
            case 6:
                a = symbolIds[0];
                b = symbolIds[1];
                c = symbolIds[2];
                d = symbolIds[3];
                e = symbolIds[4];
                f = symbolIds[5];

                if (hasWild) {
                    mask = [
                        [a, b, c, d, e, f],
                        [w, w, w, w, w, w],
                        [f, e, d, c, b, a],
                    ];
                } else {
                    if (random > 0.5) {
                        mask = [
                            [a, b, c, d, e, f],
                            [f, e, d, c, b, a],
                            [a, b, c, d, e, f],
                        ];
                    } else {
                        mask = [
                            [a, b, a, b, a, b],
                            [c, d, c, d, c, d],
                            [e, f, e, f, e, f],
                        ];
                    }
                }
                break;
            case 7:
                a = symbolIds[0];
                b = symbolIds[1];
                c = symbolIds[2];
                d = symbolIds[3];
                e = symbolIds[4];
                f = symbolIds[5];
                g = symbolIds[6];

                if (hasWild) {
                    mask = [
                        [a, b, w, c, d, w],
                        [e, w, f, w, g, w],
                        [a, b, w, c, d, w],
                    ];
                } else {
                    mask = [
                        [a, b, a, b, a, b],
                        [c, d, e, c, d, e],
                        [f, g, f, g, f, g],
                    ];
                }
                break;
            case 8:
                a = symbolIds[0];
                b = symbolIds[1];
                c = symbolIds[2];
                d = symbolIds[3];
                e = symbolIds[4];
                f = symbolIds[5];
                g = symbolIds[6];
                h = symbolIds[7];

                if (hasWild) {
                    mask = [
                        [a, w, b, w, c, w],
                        [h, g, h, g, h, g],
                        [d, w, e, w, f, w],
                    ];
                } else {
                    mask = [
                        [a, b, a, a, b, c],
                        [g, h, g, h, g, h],
                        [d, e, f, d, e, f],
                    ];
                }
                break;
        }

        return mask;
    }

    private initReflectionFilter(): void {
        if (this._reflectionContainer != null) {
            this._reflectionContainer.addElement(this.childs["Scene"]);
            this._reflectionContainer.addElement(this.childs["Reels"]);
            this._reflectionContainer.addElement(this.childs["SyncedReelsBg"]);
            this._reflectionContainer.addElement(this.childs["Machine"]);
            this._reflectionContainer.addElement(this.childs["NoWinContainer"]);
            this._reflectionContainer.addElement(this.childs["WinContainer"]);
            this._reflectionContainer.addElement(this.childs["GambleReflectionHolder"]);
            this._reflectionContainer.addElement(this.childs["HighWinSceneHolder"]);

            this._reflectionContainer.activateFilter();
        }
    }

    private setupSceneElement(): void {
        SceneHolderElement.AddElement({
            element: this._reflectionContainer.childs["Reels"],
            parent: this._reflectionContainer,
            index: this._reflectionContainer.getChildIndex(this._reflectionContainer.childs["Reels"]),
        });
        SceneHolderElement.AddElement({
            element: this._reflectionContainer.childs["SyncedReelsBg"],
            parent: this._reflectionContainer,
            index: this._reflectionContainer.getChildIndex(this._reflectionContainer.childs["SyncedReelsBg"]),
        });
        SceneHolderElement.AddElement({
            element: this._reflectionContainer.childs["NoWinContainer"],
            parent: this._reflectionContainer,
            index: this._reflectionContainer.getChildIndex(this._reflectionContainer.childs["NoWinContainer"]),
        });
        SceneHolderElement.AddElement({
            element: this._reflectionContainer.childs["WinContainer"],
            parent: this._reflectionContainer,
            index: this._reflectionContainer.getChildIndex(this._reflectionContainer.childs["WinContainer"]),
        });

        SceneHolderElement.SetParent(this._reflectionContainer.childs["HighWinSceneHolder"]);
    }

    private fadeSong(song: string, property: string, duration: number, newValue: number): void {
        Tweens.removeTweens(this, property);
        let tween = Tweens.customTween(this, {
            tween: property,
            tweenValues: [this[property], newValue],
            tweenDuration: [duration],
        });

        tween.eventCallback("onUpdate", this.onFadeSong, [song, property], this);
    }

    private onFadeSong(song: string, property: string): void {
        PlayList.setVolume(song, this[property]);
    }

    private setSongVolume(song: string, property: string, volume: number): void {
        PlayList.setVolume(song, volume);
        this[property] = volume;
    }

    private removeSongFade(property: string): void {
        Tweens.removeTweens(this, property);
    }

    /**
     * Toggle all sounds used in base game (background, effects, .etc)
     * @param enable
     */
    toggleSounds(enable: boolean): void {
        if (enable) {
            PlayList.loop("SND_BaseLoop");
        } else {
            PlayList.stop("SND_BaseLoop");
            PlayList.stop("SND_BaseRecapLoop");
        }
    }
}



/** WEBPACK FOOTER **
 ** ./Engine/Game/GameComponents/Base/BaseView.ts
 **/